/** Building blocks for the account area (reference layout: large rounded cards, pill controls). */
import { useEffect, useMemo, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import qrcode from 'qrcode-generator';
import { fmtDateTime, type KycStatus } from '@platform/shared';
import { cx, Spinner } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { Link } from '../../lib/router';

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <section className={cx('rounded-[24px] border border-line bg-card p-5 md:p-8', className)}>{children}</section>;
}

export function H2({ children, className }: { children: ReactNode; className?: string }) {
  return <h2 className={cx('m-0 text-[22px] font-semibold md:text-[26px]', className)}>{children}</h2>;
}

export function H1({ children }: { children: ReactNode }) {
  return <h1 className="m-0 text-[24px] font-semibold md:text-[30px]">{children}</h1>;
}

/** Grey track with a raised pill for the active option (Buy/Sell/Convert style). */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  size = 'md',
  className,
  label,
}: {
  options: { id: T; label: ReactNode }[];
  value: T;
  onChange: (v: T) => void;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  label: string;
}) {
  const h = size === 'lg' ? 'h-[48px] text-[16px] md:h-[52px] md:text-[18px]' : size === 'md' ? 'h-[44px] text-[15px]' : 'px-2.5 py-0.5 text-[12px]';
  return (
    <div role="radiogroup" aria-label={label} className={cx('grid auto-cols-fr grid-flow-col rounded-full bg-track p-1', className)}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          role="radio"
          aria-checked={value === o.id}
          onClick={() => onChange(o.id)}
          className={cx('flex items-center justify-center gap-1.5 rounded-full px-4 font-semibold whitespace-nowrap', h, value === o.id ? 'bg-card text-fg shadow-sm' : 'text-muted hover:text-fg')}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

type BtnKind = 'primary' | 'secondary' | 'accent' | 'danger' | 'destructive';
const BTN: Record<BtnKind, string> = {
  primary: 'bg-fg text-bg',
  secondary: 'bg-pill text-fg',
  accent: 'bg-accent text-white',
  danger: 'bg-pill text-down',
  /** Red-tinted, for an action that cannot be undone (cancel a payment). */
  destructive: 'border border-down/40 bg-down-bg text-down enabled:hover:bg-down enabled:hover:text-white',
};

export function PillButton({
  kind = 'primary',
  size = 'md',
  busy,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { kind?: BtnKind; size?: 'sm' | 'md' | 'lg'; busy?: boolean }) {
  const s = size === 'lg' ? 'h-[56px] px-8 text-[17px] md:h-[64px] md:text-[18px]' : size === 'md' ? 'h-[52px] px-7 text-[16px]' : 'h-[40px] px-5 text-[14px]';
  return (
    <button
      type="button"
      {...rest}
      disabled={rest.disabled || busy}
      className={cx('inline-flex items-center justify-center gap-2 rounded-full font-semibold disabled:cursor-not-allowed disabled:bg-track disabled:text-muted', s, BTN[kind], className)}
    >
      {busy ? <Spinner /> : children}
    </button>
  );
}

export function PillLink({ to, kind = 'primary', size = 'md', className, children }: { to: string; kind?: BtnKind; size?: 'sm' | 'md'; className?: string; children: ReactNode }) {
  const s = size === 'md' ? 'h-[52px] px-7 text-[16px]' : 'h-[40px] px-5 text-[14px]';
  return (
    <Link to={to} className={cx('inline-flex items-center justify-center gap-2 rounded-full font-semibold no-underline', s, BTN[kind], className)}>
      {children}
    </Link>
  );
}

export function Field({ label, htmlFor, hint, error, children, className }: { label: ReactNode; htmlFor?: string; hint?: ReactNode; error?: string | null; children: ReactNode; className?: string }) {
  return (
    <div className={cx('flex min-w-0 flex-col gap-2.5', className)}>
      <label htmlFor={htmlFor} className="flex items-center gap-1.5 text-[14px] font-semibold">
        {label}
      </label>
      {children}
      {hint && !error && <span className="text-[13px] text-muted">{hint}</span>}
      {error && (
        <span className="text-[13px] text-down" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

/** Pink box with a filled warning triangle (reference "We are verifying your identity…"). */
export function Alert({ children, tone = 'alert', className }: { children: ReactNode; tone?: 'alert' | 'info'; className?: string }) {
  return (
    <div className={cx('flex gap-4 rounded-[20px] p-5 text-[15px] leading-relaxed md:p-6 md:text-[16px]', tone === 'alert' ? 'bg-alert' : 'bg-track', className)}>
      {tone === 'alert' ? <WarnIcon className="shrink-0 text-alert-fg" /> : <InfoIcon className="shrink-0 text-muted" />}
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export function WarnIcon({ size = 24, className }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <path d="M12 2.5l10 18h-20z" fill="currentColor" stroke="currentColor" strokeLinejoin="round" strokeWidth={2} />
      <path d="M12 9.5v4.5M12 17.2v.01" stroke="var(--card)" strokeWidth={2.4} strokeLinecap="round" />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true" className={className}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6M12 7.5v.01" />
    </svg>
  );
}

export function Chip({ tone, children, className }: { tone: 'up' | 'down' | 'warn' | 'accent' | 'muted'; children: ReactNode; className?: string }) {
  const t = { up: 'bg-up-bg text-up', down: 'bg-down-bg text-down', warn: 'bg-warn-bg text-warn', accent: 'bg-accent-bg text-accent-fg', muted: 'bg-track text-muted' }[tone];
  return <span className={cx('inline-flex shrink-0 items-center rounded-md px-2.5 py-1 text-[13px] font-medium whitespace-nowrap', t, className)}>{children}</span>;
}

export const KYC_LABEL: Record<KycStatus, { label: string; tone: 'up' | 'down' | 'warn' | 'accent' }> = {
  none: { label: 'Not verified', tone: 'warn' },
  pending: { label: 'Verifying', tone: 'accent' },
  approved: { label: 'Verified', tone: 'up' },
  partial: { label: 'Partly verified', tone: 'warn' },
  rejected: { label: 'Rejected', tone: 'down' },
  expired: { label: 'ID expired', tone: 'warn' },
};

export function KycBadge({ status, className }: { status: KycStatus; className?: string }) {
  const k = KYC_LABEL[status];
  return (
    <Chip tone={k.tone} className={className}>
      {k.label}
    </Chip>
  );
}

/** Row in a bordered list (reference asset rows: round icon, title, subtitle, right side). */
export function Row({ icon, iconClass, title, sub, right, mono }: { icon: ReactNode; iconClass?: string; title: ReactNode; sub?: ReactNode; right?: ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-center gap-4 border-b border-line py-4 last:border-b-0 md:py-5">
      <span className={cx('grid size-11 shrink-0 place-items-center rounded-full', iconClass ?? 'bg-track')}>{icon}</span>
      <div className="min-w-0 flex-1">
        <div className={cx('truncate text-[16px] md:text-[17px]', mono && 'num')}>{title}</div>
        {sub && <div className="truncate text-[14px] text-muted md:text-[15px]">{sub}</div>}
      </div>
      {right}
    </div>
  );
}

export function List({ children, empty }: { children: ReactNode; empty?: ReactNode }) {
  const has = Array.isArray(children) ? children.length > 0 : !!children;
  return <div className="rounded-[20px] border border-line px-5">{has ? children : <div className="py-8 text-center text-[15px] text-muted">{empty}</div>}</div>;
}

/** Inline SVG icons used across the account pages. */
const PATHS = {
  down: 'M12 4v12M6 11l6 6 6-6M5 20h14',
  up: 'M12 20V8M6 13l6-6 6 6M5 4h14',
  chart: 'M3 20h18M6 16l4-5 3 3 5-7',
  help: 'M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.6v.6M12 17.5v.01M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z',
  lock: 'M5 11h14v10H5zM8 11V8a4 4 0 0 1 8 0v3',
  check: 'M5 12.5l4.5 4.5L19 7.5',
  x: 'M6 6l12 12M18 6L6 18',
  clock: 'M12 7v5l3 2M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z',
  copy: 'M9 9h11v11H9zM5 15V5a2 2 0 0 1 2-2h8',
  right: 'M9 6l6 6-6 6',
  left: 'M15 6l-6 6 6 6',
  swap: 'M4 8h14l-4-4M20 16H6l4 4',
  grid: 'M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z',
  mail: 'M3 5h18v14H3zM3 7l9 6 9-6',
  phone: 'M6 2h12v20H6zM11 18h2',
  upload: 'M12 16V4M7 9l5-5 5 5M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3',
  clip: 'M20 11.5l-8.3 8.3a5 5 0 0 1-7-7L13 4.5a3.3 3.3 0 0 1 4.7 4.7l-8.3 8.3a1.7 1.7 0 0 1-2.4-2.4L14.5 7.6',
  shield: 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z',
  wallet: 'M3 6h18v14H3zM16 13h2M3 10h18',
  bank: 'M3 10h18M12 3l9 5H3zM5 10v8M9.5 10v8M14.5 10v8M19 10v8M3 21h18',
  card: 'M3 6h18v12H3zM3 10h18M7 15h4',
  user: 'M12 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM4 21c1-4 4-6 8-6s7 2 8 6',
  trash: 'M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3',
  info: 'M12 11v6M12 7.5v.01M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z',
  doc: 'M6 3h8l5 5v13H6zM14 3v5h5M9 13h7M9 17h7',
} as const;
export type AcctIconName = keyof typeof PATHS;

export function AIcon({ name, size = 20, className }: { name: AcctIconName; size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
      <path d={PATHS[name]} />
    </svg>
  );
}

export function QrCode({ value, size = 184, className, label }: { value: string; size?: number; className?: string; label: string }) {
  const svg = useMemo(() => {
    const qr = qrcode(0, 'M');
    qr.addData(value);
    qr.make();
    const n = qr.getModuleCount();
    let d = '';
    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.isDark(r, c)) d += `M${c + 2} ${r + 2}h1v1h-1z`;
    return { d, n: n + 4 };
  }, [value]);
  return (
    <svg role="img" aria-label={label} width={size} height={size} viewBox={`0 0 ${svg.n} ${svg.n}`} shapeRendering="crispEdges" className={cx('shrink-0 rounded-[16px] border border-line bg-white', className)}>
      <rect width={svg.n} height={svg.n} fill="#ffffff" />
      <path d={svg.d} fill="#0b0c0f" />
    </svg>
  );
}

export function CopyButton({ value, children, className, kind = 'primary', size = 'md' }: { value: string; children: ReactNode; className?: string; kind?: BtnKind; size?: 'sm' | 'md' }) {
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!done) return;
    const t = window.setTimeout(() => setDone(false), 1500);
    return () => window.clearTimeout(t);
  }, [done]);
  return (
    <PillButton
      kind={kind}
      size={size}
      className={className}
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => setDone(true), () => {});
      }}
    >
      {done ? 'Copied' : children}
    </PillButton>
  );
}

/**
 * On/off switch. On is black like the primary pill (light in dark mode); the knob shows a tick when
 * on, a spinner while `busy` and a padlock when `locked`.
 */
export function Switch({ checked, onChange, label, busy, locked }: { checked: boolean; onChange: (next: boolean) => void; label: string; busy?: boolean; locked?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-busy={busy || undefined}
      disabled={busy || locked}
      onClick={() => onChange(!checked)}
      className={cx(
        'relative h-[30px] w-[52px] shrink-0 rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed motion-reduce:transition-none',
        checked ? 'bg-fg' : 'bg-pill-on',
        locked && 'opacity-40',
      )}
    >
      <span
        className={cx(
          'absolute top-[3px] left-[3px] grid size-6 place-items-center rounded-full shadow transition-transform motion-reduce:transition-none',
          checked && !busy ? 'translate-x-[22px] bg-bg text-fg' : 'bg-white text-muted',
          busy && 'translate-x-[11px]',
        )}
      >
        {busy ? (
          <span className="size-3.5 animate-spin rounded-full border-2 border-line-strong border-t-[#12141a]" />
        ) : locked ? (
          <AIcon name="lock" size={12} />
        ) : (
          checked && <AIcon name="check" size={13} />
        )}
      </span>
    </button>
  );
}

/** Six boxes over one real input, so typing, pasting and the phone's code autofill all work. */
export function CodeBoxes({ value, onChange, id, label, error, autoFocus }: { value: string; onChange: (v: string) => void; id: string; label: string; error?: boolean; autoFocus?: boolean }) {
  const [focus, setFocus] = useState(false);
  return (
    <div className="relative">
      <input
        id={id}
        aria-label={label}
        aria-invalid={error || undefined}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        className="absolute inset-0 z-10 size-full cursor-text opacity-0"
      />
      <div aria-hidden="true" className="grid grid-cols-6 gap-2">
        {Array.from({ length: 6 }, (_, i) => (
          <span
            key={i}
            className={cx(
              'num grid h-14 place-items-center rounded-xl border bg-bg text-[22px] font-medium',
              error ? 'border-down text-down' : focus && i === Math.min(value.length, 5) ? 'border-fg ring-3 ring-track' : 'border-line',
            )}
          >
            {value[i] ?? ''}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Seconds left on a countdown (a resend wait); `start(n)` begins a new one. */
export function useCountdown(): [number, (sec: number) => void] {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (left <= 0) return;
    const t = window.setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [left]);
  return [left, setLeft];
}

export const mmss = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;

export const errMsg = (e: unknown, fallback = 'Something went wrong. Try again.') => (e instanceof ApiError ? e.message : fallback);

/** Local date-time in the app-wide format: "06 Oct 16:50:58", or "06 Oct 2026, 16:50:58" with the year. */
export const when = (iso: string, withYear = false) => fmtDateTime(iso, { year: withYear });

export function Notice({ msg, onClose }: { msg: { ok: boolean; text: string } | null; onClose?: () => void }) {
  if (!msg) return null;
  return (
    <div role="status" className={cx('flex items-center justify-between gap-3 rounded-[16px] px-5 py-3 text-[15px]', msg.ok ? 'bg-up-bg text-up' : 'bg-down-bg text-down')}>
      <span>{msg.text}</span>
      {onClose && (
        <button type="button" onClick={onClose} aria-label="Dismiss" className="shrink-0 opacity-70 hover:opacity-100">
          <AIcon name="x" size={16} />
        </button>
      )}
    </div>
  );
}
